import DOMPurify from 'dompurify';

// The editor deliberately stores a very small, predictable subset of HTML.
// Keeping the allow-list here means every consumer (editor, review and detail
// page) uses the same safety boundary.
export const RICH_TEXT_ALLOWED_TAGS = [
  'p', 'div', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'ul', 'ol', 'li',
  'h2', 'h3', 'blockquote',
];

export const sanitizeRichText = (value = '') => DOMPurify.sanitize(
  String(value ?? ''),
  {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: [],
    FORBID_ATTR: ['style', 'class', 'id'],
  },
);

export const richTextToPlainText = (value = '') => {
  const clean = sanitizeRichText(value);
  // `innerText` is layout-dependent: browsers can add line breaks for list
  // containers or wrapped lines. That made the counter disagree with the
  // backend when a description was close to 5,000 characters. Count only
  // explicit breaks and the closing tags that represent blocks in the editor.
  const withBreaks = clean
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h2|h3|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  if (typeof document === 'undefined') {
    return withBreaks
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\r/g, '')
      .trim();
  }

  // Let the browser decode entities, but use textContent so visual layout
  // never changes the result.
  const holder = document.createElement('div');
  holder.innerHTML = withBreaks;
  return (holder.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '')
    .trim();
};

export const plainTextToRichText = (value = '') => {
  const escaped = DOMPurify.sanitize(String(value ?? ''), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
  return escaped.replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');
};

// Older trips contain plain text. Convert those values on the way into the
// editor/detail renderer while leaving already-formatted values intact.
export const normalizeRichTextValue = (value = '') => {
  const raw = String(value ?? '');
  if (!raw) return '';

  const clean = sanitizeRichText(raw);
  const hasSupportedMarkup = /<\s*(p|div|br|strong|b|em|i|u|s|strike|ul|ol|li|h2|h3|blockquote)\b/i.test(clean);
  return hasSupportedMarkup ? clean : plainTextToRichText(raw);
};

// Truncate text nodes while preserving the surrounding allowed markup. This
// keeps the existing 5.000-character field limit based on what the traveller
// actually wrote, rather than on formatting tags.
export const truncateRichText = (value = '', maxLength = Infinity) => {
  const normalized = normalizeRichTextValue(value);
  if (!Number.isFinite(maxLength) || maxLength < 0) return normalized;
  if (richTextToPlainText(normalized).length <= maxLength) return normalized;
  if (typeof document === 'undefined') return normalized;

  const template = document.createElement('template');
  template.innerHTML = normalized;
  let remaining = maxLength;

  const trimNode = (parent) => {
    Array.from(parent.childNodes).forEach((node) => {
      if (remaining <= 0) {
        parent.removeChild(node);
        return;
      }
      if (node.nodeType === 3) {
        const text = node.nodeValue || '';
        if (text.length > remaining) {
          node.nodeValue = text.slice(0, remaining);
          remaining = 0;
        } else {
          remaining -= text.length;
        }
        return;
      }
      trimNode(node);
    });
  };

  trimNode(template.content);
  let result = sanitizeRichText(template.innerHTML);

  // Block/list closing tags are represented as line breaks by
  // richTextToPlainText.  A text-node-only pass above cannot see those
  // structural characters, so a description that ends exactly at the limit
  // can still be one or more visible characters too long. Trim only the
  // necessary tail characters, preserving the formatting around the text.
  const overflow = richTextToPlainText(result).length - maxLength;
  if (overflow > 0) {
    const amount = { value: overflow };
    const trimTextNodesFromEnd = (parent) => {
      const nodes = Array.from(parent.childNodes).reverse();
      for (const node of nodes) {
        if (amount.value <= 0) break;
        if (node.nodeType === 3) {
          const text = node.nodeValue || '';
          const remove = Math.min(text.length, amount.value);
          node.nodeValue = text.slice(0, text.length - remove);
          amount.value -= remove;
        } else if (node.nodeType === 1) {
          trimTextNodesFromEnd(node);
        }
      }
    };

    trimTextNodesFromEnd(template.content);
    result = sanitizeRichText(template.innerHTML);
  }

  return result;
};
